import { getApi, postApi } from "./resonanceApi";
import { AxiosResponse } from 'axios';
import { SiteType, StudyContentType } from "../types/welcome";

export enum StudyApiUrlsEnum {
  GET_CONTENT = 'studies/{studyId}/contents/{contentId}',
  GET_CONTENT_PREVIEW = 'studies/{studyId}/contents/{contentId}/preview',
  GET_ARTICLES = 'studies/{studyId}/articles',
  GET_SITES = 'studies/{studyId}/sites',
  LEAVE_STUDY = 'studies/{studyId}/users/current/leave',
  GET_STUDY_LANGS = 'studies/{studyId}/langs',
  GET_STUDY_IS_GAMIFIED = 'studies/{studyId}/is_gamified',
}

/** Card for the Resources page — derived server-side from the article's own blocks. */
export interface ArticleCardType {
  id: number;
  title: string;
  description: string;
  image: string | null;
  avg_amount_time_to_read: number;
}

export const getStudyArticles = ({ studyId, lang }: { studyId: number; lang: string }): Promise<AxiosResponse<ArticleCardType[]>> => {
  const url = StudyApiUrlsEnum.GET_ARTICLES;
  return getApi<ArticleCardType[]>(url, { studyId }, { lang });
};

export const getStudyContent = ({ studyId, contentId, lang }): Promise<AxiosResponse<StudyContentType>> => {
  const url = StudyApiUrlsEnum.GET_CONTENT;
  return getApi<StudyContentType>(url, { studyId, contentId }, { lang });
};

// No-login content "Preview" read — carries the signed ?token= minted by Diapason
// (mirrors the questionnaire Simulate pattern).
export const getApiContentPreview = (studyId: number, contentId: number, lang: string, token: string): Promise<AxiosResponse<StudyContentType>> => {
  const url = StudyApiUrlsEnum.GET_CONTENT_PREVIEW;
  return getApi<StudyContentType>(url, { studyId, contentId }, { lang, token });
};

export const getStudySites = ({ studyId }): Promise<AxiosResponse<SiteType[]>> => {
  const url = StudyApiUrlsEnum.GET_SITES;
  return getApi<SiteType[]>(url, { studyId });
};

interface LangsResponseType {
  additional_langs: string[];
  primary_lang: string;
}

export const getStudyLangs = (studyId: number): Promise<AxiosResponse<LangsResponseType>> => {
  const url = StudyApiUrlsEnum.GET_STUDY_LANGS;
  return getApi<LangsResponseType>(url, { studyId });
};

/**
 * Cached read of a study's languages — same reason as the invitation configs:
 * the Compte page's « Langue » row hangs off `additional_langs`, and fetching it
 * on mount inserted the row after first paint, pushing everything below it down.
 * Warmed at app entry (Tabs.tsx); `peekStudyLangs` seeds initial state synchronously.
 */
let studyLangsCache: { [studyId: number]: LangsResponseType } = {};

export const resetStudyLangsCache = () => {
  studyLangsCache = {};
};

/** Cached langs, or null when nothing has been fetched for this study yet. */
export const peekStudyLangs = (
  studyId: number | undefined
): LangsResponseType | null => (studyId ? studyLangsCache[studyId] ?? null : null);

export const getCachedStudyLangs = async (
  studyId: number
): Promise<LangsResponseType> => {
  const cached = peekStudyLangs(studyId);
  if (cached) return cached;

  const { data } = await getStudyLangs(studyId);
  studyLangsCache[studyId] = data;
  return data;
};

export const getStudyIsGamified = async (studyId: number): Promise<AxiosResponse<boolean>> => {
  const url = StudyApiUrlsEnum.GET_STUDY_IS_GAMIFIED;
  return getApi<boolean>(url, { studyId });
};

export const leaveStudy = (
  studyId: number,
  body: { allow_access_data: boolean; current_password?: string }
): Promise<AxiosResponse> => {
  const url = StudyApiUrlsEnum.LEAVE_STUDY;
  return postApi(url, { studyId }, body);
}